import type { Event } from '../types/api';
import {
  getEventAddress,
  getEventDateTimeDisplay,
  getEventFormat,
  getEventLocationLabel,
  getRelationshipText,
  getTimeOfDay,
  getVenueLabel,
  getVibes,
  EVENT_FORMAT_LABELS,
} from './eventPresentation';
import { getEventCoordinates, cleanStringArray, formatPrice, formatSpeaker, formatAudienceLevel } from './eventFields';
import { formatEventLocalDateTime } from './eventDateTime';
import { buildEventUrl, buildExportFilename } from './slug';

/**
 * CSV / Markdown / plain-text export of the current, filtered event view.
 *
 * Exports always contain the full untruncated values and the canonical event
 * URL, and they preserve the ordering the user is looking at (upcoming first,
 * then recent past) — including recent-past rows while that section is
 * collapsed.
 */

export type ExportFormat = 'csv' | 'markdown' | 'text';

export interface ExportColumn {
  header: string;
  value: (event: Event) => string;
}

function joinList(values: unknown): string {
  return cleanStringArray(values).join('; ');
}

/** The full export schema. Order here is the column order in every format. */
export const EXPORT_COLUMNS: ExportColumn[] = [
  { header: 'Event ID', value: (e) => String(e.eventId ?? '') },
  { header: 'Title', value: (e) => e.title ?? '' },
  { header: 'Description', value: (e) => (e.description ?? '').replace(/\s+/g, ' ').trim() },
  { header: 'Start date', value: (e) => e.startDate ?? '' },
  { header: 'End date', value: (e) => e.endDate ?? '' },
  { header: 'Weekday', value: (e) => getEventDateTimeDisplay(e).weekday },
  { header: 'Times', value: (e) => getEventDateTimeDisplay(e).timeLabel },
  { header: 'Time zone', value: (e) => getEventDateTimeDisplay(e).timeZoneAbbreviation ?? '' },
  { header: 'Duration', value: (e) => getEventDateTimeDisplay(e).duration },
  { header: 'Format', value: (e) => EVENT_FORMAT_LABELS[getEventFormat(e)] },
  { header: 'Venue', value: (e) => getVenueLabel(e) },
  { header: 'Address', value: (e) => getEventAddress(e) },
  { header: 'City', value: (e) => e.location?.city ?? e.city ?? '' },
  { header: 'State', value: (e) => e.location?.state ?? e.state ?? '' },
  { header: 'Country', value: (e) => e.location?.country ?? e.country ?? '' },
  { header: 'Latitude', value: (e) => String(getEventCoordinates(e)?.latitude ?? '') },
  { header: 'Longitude', value: (e) => String(getEventCoordinates(e)?.longitude ?? '') },
  { header: 'Entities and relationships', value: (e) => getRelationshipText(e) },
  { header: 'Time of day', value: (e) => getTimeOfDay(e) },
  { header: 'Vibes', value: (e) => getVibes(e).join('; ') },
  { header: 'Event type', value: (e) => e.type ?? '' },
  { header: 'Audience level', value: (e) => formatAudienceLevel(e.audienceLevel) },
  { header: 'Topics', value: (e) => joinList(e.topics) },
  { header: 'Industries', value: (e) => joinList(e.industries) },
  { header: 'Target roles', value: (e) => joinList(e.targetRoles) },
  {
    header: 'Speakers',
    value: (e) => (Array.isArray(e.speakers) ? e.speakers.map(formatSpeaker).filter(Boolean).join('; ') : ''),
  },
  { header: 'Price', value: (e) => formatPrice(e.price, e.currency, e.isFree) },
  { header: 'Capacity', value: (e) => (typeof e.capacity === 'number' ? String(e.capacity) : '') },
  { header: 'Tickets remaining', value: (e) => (typeof e.ticketsRemaining === 'number' ? String(e.ticketsRemaining) : '') },
  { header: 'Registration deadline', value: (e) => e.registrationDeadline ?? '' },
  { header: 'Registration URL', value: (e) => e.registrationUrl ?? '' },
  { header: 'Source URLs', value: (e) => joinList(e.eventSources) },
  { header: 'Event website', value: (e) => e.eventUrl ?? '' },
  { header: 'Canonical URL', value: (e) => buildEventUrl(e) },
];

/**
 * Neutralize spreadsheet formula injection. Excel/Sheets execute any cell that
 * begins with =, +, -, @ or a control character, so those are prefixed with a
 * single quote before quoting.
 */
export function sanitizeCsvValue(value: string): string {
  if (!value) return '';
  return /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;
}

function csvCell(value: string): string {
  const safe = sanitizeCsvValue(value);
  return `"${safe.replace(/"/g, '""')}"`;
}

export function toCsv(events: Event[]): string {
  const header = EXPORT_COLUMNS.map((c) => csvCell(c.header)).join(',');
  const rows = events.map((event) =>
    EXPORT_COLUMNS.map((column) => csvCell(column.value(event) ?? '')).join(','),
  );
  // CRLF + BOM keep Excel happy with UTF-8 content.
  return `\uFEFF${[header, ...rows].join('\r\n')}\r\n`;
}

function escapeMarkdown(value: string): string {
  return value.replace(/\|/g, '\\|').replace(/\r?\n/g, ' ');
}

export function toMarkdown(events: Event[], title: string): string {
  const lines: string[] = [`# ${title}`, '', `${events.length} event${events.length === 1 ? '' : 's'}`, ''];

  for (const event of events) {
    const when = getEventDateTimeDisplay(event);
    lines.push(`## ${event.title ?? 'Untitled event'}`);
    lines.push('');

    const facts: Array<[string, string]> = [
      ['When', [when.weekday, when.dateLabel, when.timeLabel].filter(Boolean).join(', ')],
      ['Format', EVENT_FORMAT_LABELS[getEventFormat(event)]],
      ['Venue', getVenueLabel(event)],
      ['Address', getEventAddress(event)],
      ['Location', getEventLocationLabel(event)],
      ['Entities', getRelationshipText(event)],
      ['Time of day', getTimeOfDay(event)],
      ['Vibes', getVibes(event).join(', ')],
      ['Type', event.type ?? ''],
      ['Duration', when.duration],
      ['Audience', formatAudienceLevel(event.audienceLevel)],
      ['Topics', joinList(event.topics)],
      ['Industries', joinList(event.industries)],
      ['Speakers', Array.isArray(event.speakers) ? event.speakers.map(formatSpeaker).filter(Boolean).join(', ') : ''],
      ['Price', formatPrice(event.price, event.currency, event.isFree)],
      ['Register by', formatEventLocalDateTime(event.registrationDeadline)],
      ['Registration', event.registrationUrl ?? ''],
      ['Source', event.eventUrl ?? ''],
      ['Event page', buildEventUrl(event)],
    ];

    for (const [label, value] of facts) {
      if (value) lines.push(`- **${label}:** ${escapeMarkdown(value)}`);
    }

    if (event.description) {
      lines.push('', event.description.trim());
    }
    lines.push('');
  }

  return lines.join('\n');
}

export function toPlainText(events: Event[], title: string): string {
  const lines: string[] = [title, '='.repeat(title.length), '', `${events.length} event${events.length === 1 ? '' : 's'}`, ''];

  for (const event of events) {
    const when = getEventDateTimeDisplay(event);
    lines.push(event.title ?? 'Untitled event');
    for (const column of EXPORT_COLUMNS) {
      if (column.header === 'Title' || column.header === 'Description') continue;
      const value = column.value(event);
      if (value) lines.push(`  ${column.header}: ${value}`);
    }
    if (event.description) {
      lines.push(`  Description: ${event.description.replace(/\s+/g, ' ').trim()}`);
    }
    lines.push(`  Weekday/Date: ${[when.weekday, when.dateLabel].filter(Boolean).join(', ')}`);
    lines.push('');
  }

  return lines.join('\n');
}

const MIME_TYPES: Record<ExportFormat, string> = {
  csv: 'text/csv;charset=utf-8',
  markdown: 'text/markdown;charset=utf-8',
  text: 'text/plain;charset=utf-8',
};

const EXTENSIONS: Record<ExportFormat, string> = {
  csv: 'csv',
  markdown: 'md',
  text: 'txt',
};

export function buildExport(events: Event[], format: ExportFormat, title: string): string {
  if (format === 'csv') return toCsv(events);
  if (format === 'markdown') return toMarkdown(events, title);
  return toPlainText(events, title);
}

/** Trigger a browser download for the supplied events. */
export function downloadEventExport(events: Event[], format: ExportFormat, label: string): void {
  const title = `${label} events`;
  const content = buildExport(events, format, title);
  const filename = buildExportFilename(label, EXTENSIONS[format]);

  const blob = new Blob([content], { type: MIME_TYPES[format] });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  // Revoking synchronously can cancel the download in some browsers.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
